import { globalConfirm } from '../components/ConfirmDialog';
import { SortableTableHeader, useSortState } from '../components/SortableTableHeader';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppShell from '../components/layout/AppShell';
import { PageHeader, DataTableToolbar, PaginationBar, ActiveBadge, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import api, { getApiErrorMessage } from '../services/api';
import ModuleReports from './Reports/ModuleReports';
import { TERMS_REPORTS } from './Reports/reportConfigs';
import { BarChart2, FileText as FileTextIcon } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { Plus, Download, Upload, ChevronDown, X, Search, Check } from 'lucide-react';
import DataFilesAnalytics from './DataFilesAnalytics/DataFilesAnalytics';
import { termsConditionsConfig } from './DataFilesAnalytics/configs';
import { LayoutDashboard as AnalyticsIcon } from 'lucide-react';

const EMPTY = {
  code: '', outline: '', body: '',
  order_default: false, request_default: false, rfq_default: false,
  document_types: [], active: true, file_type: 'text',
};

// ── Multi-select dropdown for Document Types ─────────────────────────────────
function DocTypeMultiSelect({ selected, onChange, allDocTypes }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = allDocTypes.filter(dt =>
    !search ||
    dt.code.toLowerCase().includes(search.toLowerCase()) ||
    dt.description.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (code) => {
    onChange(
      selected.includes(code)
        ? selected.filter(c => c !== code)
        : [...selected, code]
    );
  };

  const removeOne = (e, code) => {
    e.stopPropagation();
    onChange(selected.filter(c => c !== code));
  };

  const selectAll = () => onChange(allDocTypes.map(dt => dt.code));
  const clearAll  = () => { onChange([]); setSearch(''); };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full min-h-[38px] rounded-lg border border-input bg-background px-3 py-1.5 text-sm text-left flex items-start gap-2 hover:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none transition-colors"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <div className="flex-1 flex flex-wrap gap-1">
          {selected.length === 0 ? (
            <span className="text-muted-foreground py-0.5">Select document types…</span>
          ) : (
            selected.map(code => {
              const dt = allDocTypes.find(d => d.code === code);
              return (
                <span key={code}
                  className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-medium rounded-md px-2 py-0.5 border border-primary/20">
                  <span className="font-mono">{code}</span>
                  {dt && <span className="text-primary/70 hidden sm:inline truncate max-w-[100px]"> — {dt.description}</span>}
                  <button type="button" onClick={e => removeOne(e, code)} className="ml-0.5 hover:text-destructive" aria-label={`Remove ${code}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground flex-shrink-0 mt-1 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute z-50 w-full mt-1 bg-card border border-border rounded-xl shadow-xl overflow-hidden"
          style={{ maxHeight: '320px', display: 'flex', flexDirection: 'column' }}
        >
          {/* Search + bulk actions */}
          <div className="px-3 py-2 border-b space-y-2 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search document types…"
                className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-3 text-xs focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                autoFocus
                aria-label="Search document types"
              />
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{selected.length} of {allDocTypes.length} selected</span>
              <div className="flex gap-2">
                <button type="button" onClick={selectAll} className="text-primary hover:underline font-medium">Select all</button>
                <span className="text-border">·</span>
                <button type="button" onClick={clearAll} className="text-muted-foreground hover:text-destructive hover:underline">Clear</button>
              </div>
            </div>
          </div>

          {/* Scrollable option list */}
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No document types match "{search}"</p>
            ) : (
              <ul>
                {filtered.map(dt => {
                  const isSelected = selected.includes(dt.code);
                  return (
                    <li key={dt.code}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => toggle(dt.code)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-accent ${isSelected ? 'bg-primary/5' : ''}`}
                      >
                        {/* Checkbox visual */}
                        <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                          isSelected ? 'bg-primary border-primary' : 'border-input'
                        }`}>
                          {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                        </div>
                        {/* Code + description */}
                        <span className="font-mono font-semibold text-primary w-20 flex-shrink-0">{dt.code}</span>
                        <span className="text-muted-foreground truncate">{dt.description}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function TermsConditionsPage() {
  const [records, setRecords] = useState([]);
  const { canWrite: _canWrite } = usePermissions();
  const canWriteData = _canWrite('terms_conditions');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [viewTab, setViewTab] = useState('analytics');
  const [loading, setLoading] = useState(false);
  const [sortState, handleSort] = useSortState('code', 'asc');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  // All document types from the database
  const [allDocTypes, setAllDocTypes] = useState([]);

  // Load T&Cs
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/data/terms-conditions', { params: { search, page, page_size: pageSize, sort_by: sortState.field, sort_dir: sortState.dir } });
      setRecords(res.data.data || []); setTotal(res.data.total);
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load'); }
    setLoading(false);
  }, [search, page, pageSize, sortState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load all document types (all pages) for the multi-select
  const loadDocTypes = useCallback(async () => {
    try {
      const res = await api.get('/api/data/document-types', { params: { page_size: 100 } });
      setAllDocTypes(res.data.data || []);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDocTypes(); }, [loadDocTypes]);

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const openCreate = () => { setForm({ ...EMPTY, document_types: [] }); setIsEdit(false); setModalOpen(true); };
  const openEdit   = r   => { setForm({ ...EMPTY, ...r, document_types: r.document_types || [] }); setIsEdit(true); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) await api.put(`/api/data/terms-conditions/${form.code}`, form);
      else        await api.post('/api/data/terms-conditions', form);
      toast.success(isEdit ? 'Updated' : 'Created');
      setModalOpen(false); load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const deleteRecord = async () => {
    if (!await globalConfirm('Delete this T&C?')) return;
    try { await api.delete(`/api/data/terms-conditions/${form.code}`); toast.success('Deleted'); setModalOpen(false); load(); }
    catch (err) { if (err?.response?.status >= 500) toast.error('Delete failed'); }
  };

  // Compact display of linked doc types for the list column
  const renderDocTypes = (codes = []) => {
    if (!codes.length) return <span className="text-muted-foreground text-xs">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {codes.slice(0, 4).map(code => (
          <span key={code} className="text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded border">{code}</span>
        ))}
        {codes.length > 4 && (
          <span className="text-[10px] text-muted-foreground">+{codes.length - 4}</span>
        )}
      </div>
    );
  };


  const toggleFav = async (r) => {
    const { toggleFavorite } = await import('../utils/favorites');
    const result = await toggleFavorite('terms-conditions', r.code);
    if (result !== null) {
      setRecords(prev => {
        const updated = prev.map(rec => (rec === r) ? { ...rec, is_favorite: result } : rec);
        const starred = updated.filter(rec => rec.is_favorite);
        const unstarred = updated.filter(rec => !rec.is_favorite);
        return [...starred, ...unstarred];
      });
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Terms & Conditions"
        description={`Total records: ${total}`}
        helpGuide="data-files"
        actions={viewTab === 'list' ? <>
          <Button size="sm" variant="outline"><Download className="h-4 w-4 mr-1" />Export</Button>
          <Button size="sm" onClick={openCreate} style={canWriteData ? {} : {display:"none"}} data-testid="terms-add-button">
            <Plus className="h-4 w-4 mr-1" />Add New
          </Button>
        </> : null}
      />
      <div className="flex gap-4 border-b mb-4" data-testid="terms-view-tabs">
        <button onClick={() => setViewTab('analytics')} data-testid="terms-tab-analytics"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'analytics' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <AnalyticsIcon className="h-4 w-4 inline mr-1.5" /> Analytics
        </button>
        <button onClick={() => setViewTab('list')} data-testid="terms-tab-list"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'list' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <FileTextIcon className="h-4 w-4 inline mr-1.5" /> Terms & Conditions
        </button>
        <button onClick={() => setViewTab('reports')} data-testid="terms-tab-reports"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'reports' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <BarChart2 className="h-4 w-4 inline mr-1.5" /> Reports
        </button>
      </div>
      {viewTab === 'analytics' ? (
        <DataFilesAnalytics {...termsConditionsConfig} onCardClick={() => setViewTab('list')} />
      ) : viewTab === 'reports' ? (
        <ModuleReports reports={TERMS_REPORTS} apiPrefix="/api/module-reports/data" module="Terms & Conditions" />
      ) : (<>
      <DataTableToolbar
        searchValue={search}
        onSearch={v => { setSearch(v); setPage(1); }}
        onClear={() => { setSearch(''); setPage(1); }}
        searchPlaceholder="Search T&Cs…"
      />

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? <div className="py-12"><LoadingSpinner /></div> :
        records.length === 0 ? (
          <EmptyState title="No T&Cs found" action={<Button size="sm" onClick={openCreate} style={canWriteData ? {} : {display:"none"}}>Add T&C</Button>} />
        ) : (
          <Table className="data-table" aria-label="Terms and conditions">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <SortableTableHeader label="Code"             field="code"             sortState={sortState} onSort={handleSort} className="whitespace-nowrap" />
                <SortableTableHeader label="Outline"          field="outline"          sortState={sortState} onSort={handleSort} />
                <SortableTableHeader label="Document Types"   field="document_types"   sortState={sortState} onSort={handleSort} />
                <SortableTableHeader label="Order Default"    field="order_default"    sortState={sortState} onSort={handleSort} />
                <SortableTableHeader label="Request Default"  field="request_default"  sortState={sortState} onSort={handleSort} />
                <SortableTableHeader label="RFQ Default"      field="rfq_default"      sortState={sortState} onSort={handleSort} />
                <SortableTableHeader label="Active"           field="active"           sortState={sortState} onSort={handleSort} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map(r => (
                <TableRow key={r.code} className="cursor-pointer" onClick={() => openEdit(r)}>
                  <TableCell className="font-mono font-semibold text-primary whitespace-nowrap">{r.code}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{r.outline}</TableCell>
                  <TableCell>{renderDocTypes(r.document_types)}</TableCell>
                  <TableCell><ActiveBadge active={r.order_default} /></TableCell>
                  <TableCell><ActiveBadge active={r.request_default} /></TableCell>
                  <TableCell><ActiveBadge active={r.rfq_default} /></TableCell>
                  <TableCell><ActiveBadge active={r.active} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className="px-4">
          <PaginationBar page={page} pageSize={pageSize} total={total}
            onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} />
        </div>
      </div>
      </>)}

      {/* ── Edit / Create Modal ── */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              {isEdit ? 'Edit Terms & Conditions' : 'Add Terms & Conditions'}
              <div className="flex items-center gap-2 mr-6">
                <span className="text-sm font-normal text-muted-foreground">Active</span>
                <Switch checked={form.active} onCheckedChange={v => f('active', v)} />
              </div>
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="text">
            <TabsList>
              <TabsTrigger value="text">Text</TabsTrigger>
              <TabsTrigger value="file">File</TabsTrigger>
            </TabsList>

            <TabsContent value="text" className="pt-4 space-y-5">
              {/* Code + Outline */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Code *</Label>
                  <Input value={form.code} onChange={e => f('code', e.target.value)} disabled={isEdit}
                    placeholder="e.g. TC-001" />
                </div>
                <div className="space-y-1.5">
                  <Label>Outline (Short Title) *</Label>
                  <Input value={form.outline} onChange={e => f('outline', e.target.value)}
                    placeholder="e.g. Payment Terms" />
                </div>
              </div>

              {/* Document Types — multi-select from DB */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Applies to Document Types</Label>
                  <span className="text-xs text-muted-foreground">
                    {allDocTypes.length} document types available · {form.document_types.length} selected
                  </span>
                </div>
                {allDocTypes.length === 0 ? (
                  <div className="h-10 flex items-center px-3 rounded-lg border border-input bg-muted/30 text-sm text-muted-foreground">
                    Loading document types…
                  </div>
                ) : (
                  <DocTypeMultiSelect
                    selected={form.document_types || []}
                    onChange={codes => f('document_types', codes)}
                    allDocTypes={allDocTypes}
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  This T&C will be available when creating documents of the selected type(s).
                </p>
              </div>

              {/* Body Text */}
              <div className="space-y-1.5">
                <Label>Body Text</Label>
                <Textarea
                  value={form.body}
                  onChange={e => f('body', e.target.value)}
                  rows={8}
                  placeholder="Enter the full terms and conditions text here…"
                  className="font-mono text-xs resize-y"
                />
              </div>

              {/* Default flags */}
              <div className="flex flex-wrap gap-5 pt-1">
                {[
                  ['order_default',   'Order Default',   'Auto-attach to all Purchase Orders'],
                  ['request_default', 'Request Default', 'Auto-attach to all Requisitions'],
                  ['rfq_default',     'RFQ Default',     'Auto-attach to all RFQ/RFPs'],
                ].map(([key, label, hint]) => (
                  <label key={key} className="flex items-start gap-2.5 cursor-pointer group">
                    <div className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                      form[key] ? 'bg-primary border-primary' : 'border-input group-hover:border-primary/50'
                    }`}>
                      <input type="checkbox" className="sr-only" checked={form[key]}
                        onChange={e => f(key, e.target.checked)} />
                      {form[key] && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-none">{label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
                    </div>
                  </label>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="file" className="pt-4">
              <div className="border-2 border-dashed rounded-xl p-8 flex flex-col items-center gap-3">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Upload a PDF or Word document containing the T&C text</p>
                <Button size="sm" variant="outline"
                  onClick={() => toast.info('Save the T&C first, then upload via the file endpoint')}>
                  Select File
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            {isEdit && <Button variant="destructive" onClick={deleteRecord}>Delete</Button>}
            <Button onClick={save} disabled={!canWriteData} disabled={saving || !form.code || !form.outline}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
